// Documentos PDF do relatório — escritos para o GESTOR, não para o técnico.
//
// Cada relatório abre com um veredito em uma frase e os números grandes, e só
// depois vem o detalhe. Todo problema diz o que significa e o que fazer, em
// português de quem não mexe com API. O código da Meta aparece pequeno, para
// quem precisar levar ao suporte.
//
// Estas funções são puras: recebem os dados já buscados e devolvem a definição
// do documento no formato da pdfmake. Quem baixa o arquivo é o index.js, que
// carrega a biblioteca só na hora do clique.

import { NOME_PRODUTO, NOME_SELO, NOME_PREFIXO, NOME_SUFIXO } from '../marca'
import { PRIORIDADES } from '../relatorioMotivos'

// ---- paleta (o PDF é sempre claro: é papel) ----
const PRI = '#00a9d6'
const INK = '#17202a'
const SUB = '#5a6672'
const FAINT = '#8b96a1'
const RULE = '#e3e8ec'
const SOFT = '#f4f7f9'
const OK = '#079669'
const BAD = '#d63a3a'
const ATT = '#c97d06'

const LARGURA = 515 // A4 (595pt) menos as margens de 40pt

// Mesmas cores do selo de categoria da tabela do painel (TemplateCategoryBadge).
const CATEGORIAS = {
  UTILITY: { rotulo: 'Utilidade', cor: '#2563eb', explica: 'Avisos ligados a algo que o cliente já contratou: fatura, boleto, bloqueio. É o tipo mais barato.' },
  MARKETING: { rotulo: 'Marketing', cor: '#7c3aed', explica: 'Promoções e ofertas. A Meta cobra bem mais por elas, e é o tipo que mais recebe denúncias.' },
  MARKETING_LITE: { rotulo: 'Marketing', cor: '#7c3aed', explica: 'Promoções e ofertas. A Meta cobra bem mais por elas, e é o tipo que mais recebe denúncias.' },
  AUTHENTICATION: { rotulo: 'Autenticação', cor: '#b45309', explica: 'Códigos de acesso e de confirmação. Preço parecido com o de Utilidade.' },
  SERVICE: { rotulo: 'Serviço', cor: '#64748b', explica: 'Respostas dentro de uma conversa aberta pelo cliente. Sem custo na Meta.' },
}

// ---- formatação ----
const TZ = 'America/Sao_Paulo'
export const fmtInt = (n) => Number(n || 0).toLocaleString('pt-BR')
export const fmtPct = (v, casas = 1) =>
  `${(Number(v || 0) * 100).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`
export const fmtBRL = (v, casas = 2) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}`
// Preço unitário: tarifa pode ter fração de centavo (R$ 0,065) — até 4 casas.
export const fmtPreco = (v) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
const fmtData = (d) => new Date(d).toLocaleDateString('pt-BR', { timeZone: TZ })
const fmtDataHora = (d) =>
  new Date(d).toLocaleString('pt-BR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
const fmtGeradoEm = (d) => {
  const data = new Date(d)
  return `${fmtData(data)} às ${data.toLocaleTimeString('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}`
}

// Telefone do cadastro no formato que o gestor digita no sistema dele.
export function fmtTelefone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  const local = d.length >= 12 && d.startsWith('55') ? d.slice(2) : d
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`
  return raw || '—'
}

// Mistura a cor com o branco — o "fundo clarinho" de chips e caixas.
function tint(hex, alpha) {
  const n = parseInt(hex.slice(1), 16)
  const mix = (c) => Math.round(c * alpha + 255 * (1 - alpha))
  const r = mix((n >> 16) & 255)
  const g = mix((n >> 8) & 255)
  const b = mix(n & 255)
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
}

const plural = (n, um, varios) => `${fmtInt(n)} ${n === 1 ? um : varios}`

// ---- peças ----
function marca() {
  return {
    columns: [
      {
        width: 'auto',
        table: { body: [[{ text: NOME_SELO, bold: true, color: '#ffffff', fontSize: 6.5, fillColor: PRI, margin: [3, 2, 3, 1] }]] },
        layout: 'noBorders',
      },
      { width: 'auto', text: [{ text: NOME_PREFIXO, color: SUB }, { text: NOME_SUFIXO, bold: true }], fontSize: 11, margin: [5, 0, 0, 0] },
    ],
  }
}

const regua = (margem = [0, 8, 0, 0]) => ({
  canvas: [{ type: 'line', x1: 0, y1: 0, x2: LARGURA, y2: 0, lineWidth: 0.7, lineColor: RULE }],
  margin: margem,
})

function cabecalho({ eyebrow, titulo, meta }) {
  return [
    { text: eyebrow.toUpperCase(), fontSize: 7.5, bold: true, color: PRI, characterSpacing: 1.2, margin: [0, 2, 0, 0] },
    { text: titulo, fontSize: 19, bold: true, margin: [0, 4, 0, 3], lineHeight: 1.1 },
    { text: meta, fontSize: 8.5, color: SUB },
  ]
}

function chip(texto, cor, { cheio = true } = {}) {
  return {
    table: {
      body: [[{
        text: texto,
        bold: true,
        fontSize: 6.5,
        characterSpacing: 0.6,
        color: cheio ? '#ffffff' : cor,
        fillColor: cheio ? cor : tint(cor, 0.12),
        margin: [4, 2, 4, 1],
        noWrap: true,
      }]],
    },
    layout: 'noBorders',
  }
}

function veredito({ rotulo, cor, texto }) {
  return {
    table: {
      widths: ['*'],
      body: [[{
        fillColor: tint(cor, 0.09),
        margin: [10, 9, 10, 9],
        columns: [
          { width: 'auto', ...chip(rotulo, cor), margin: [0, 1, 9, 0] },
          { width: '*', text: texto, fontSize: 9.5, lineHeight: 1.35 },
        ],
      }]],
    },
    layout: 'noBorders',
    margin: [0, 14, 0, 0],
  }
}

function blocoNumeros(itens) {
  return {
    table: {
      widths: itens.map(() => '*'),
      body: [itens.map((it) => ({
        stack: [
          { text: it.rotulo.toUpperCase(), fontSize: 6.5, bold: true, color: SUB, characterSpacing: 0.7 },
          { text: it.valor, fontSize: 16, bold: true, color: it.cor || INK, margin: [0, 3, 0, 1] },
          { text: it.dica || ' ', fontSize: 7, color: FAINT },
        ],
        margin: [9, 8, 9, 8],
      }))],
    },
    layout: {
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      hLineColor: () => RULE,
      vLineColor: () => RULE,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 12, 0, 0],
  }
}

function secao(texto, extra) {
  return {
    text: [{ text: texto, bold: true }, extra ? { text: `   ${extra}`, color: FAINT, fontSize: 8 } : ''],
    fontSize: 11,
    margin: [0, 18, 0, 7],
  }
}

// Layout de tabela de dados: só réguas horizontais, cabeçalho cinza.
const layoutTabela = {
  hLineWidth: (i) => (i === 0 ? 0 : 0.6),
  vLineWidth: () => 0,
  hLineColor: () => RULE,
  fillColor: (i) => (i === 0 ? SOFT : null),
  paddingLeft: () => 6,
  paddingRight: () => 6,
  paddingTop: () => 5,
  paddingBottom: () => 5,
}

const th = (texto, alinhamento = 'left') => ({
  text: texto.toUpperCase(),
  fontSize: 6.5,
  bold: true,
  color: SUB,
  characterSpacing: 0.6,
  alignment: alinhamento,
})

function bolinha(cor) {
  return { canvas: [{ type: 'ellipse', x: 3.5, y: 5, r1: 3.5, r2: 3.5, color: cor }], width: 10 }
}

function barrinha(proporcao, cor, largura = 44) {
  const w = Math.max(3, Math.round(largura * Math.min(1, Math.max(0, proporcao))))
  return { canvas: [{ type: 'rect', x: 0, y: 0, w, h: 3.5, r: 1.5, color: cor }], margin: [0, 3, 0, 0] }
}

function barraProporcao(partes) {
  const total = partes.reduce((a, p) => a + p.valor, 0) || 1
  let x = 0
  const retangulos = partes.map((p) => {
    const w = Math.max(p.valor > 0 ? 3 : 0, (p.valor / total) * LARGURA)
    const r = { type: 'rect', x, y: 0, w, h: 8, color: p.cor }
    x += w
    return r
  })
  return [
    { canvas: retangulos, margin: [0, 10, 0, 0] },
    {
      columns: partes.map((p, i) => ({
        text: `${p.rotulo} · ${fmtPct(p.valor / total)}`,
        fontSize: 7.5,
        color: SUB,
        alignment: i === partes.length - 1 ? 'right' : 'left',
      })),
      margin: [0, 4, 0, 0],
    },
  ]
}

// Gráfico de barras empilhadas por dia. Os rótulos ficam FORA do desenho,
// posicionados sobre a mesma escala, porque texto dentro do vetor não usa a
// fonte do PDF.
function graficoPorDia({ dias, series }) {
  const eixo = 22
  const largura = LARGURA - eixo
  const altura = 104
  const totais = dias.map((_, i) => series.reduce((a, s) => a + (s.valores[i] || 0), 0))
  const maior = Math.max(1, ...totais)
  // topo "redondo" e par, para a linha do meio também cair num inteiro
  const passo = maior <= 10 ? 2 : maior <= 50 ? 10 : Math.pow(10, Math.floor(Math.log10(maior)))
  const topo = Math.ceil(maior / passo) * passo
  const slot = largura / dias.length
  const bw = Math.max(2, slot * 0.66)
  const y = (v) => altura - (v / topo) * altura
  const marcas = [0, topo / 2, topo]

  const desenho = marcas.map((v) => ({
    type: 'line', x1: 0, y1: y(v), x2: largura, y2: y(v), lineWidth: 0.5, lineColor: RULE,
  }))
  dias.forEach((_, i) => {
    let acumulado = 0
    series.forEach((s) => {
      const v = s.valores[i] || 0
      if (!v) return
      const y1 = y(acumulado + v)
      desenho.push({ type: 'rect', x: i * slot + (slot - bw) / 2, y: y1, w: bw, h: Math.max(0.8, y(acumulado) - y1), color: s.cor })
      acumulado += v
    })
  })

  const cada = Math.max(1, Math.ceil(dias.length / 7))
  return {
    unbreakable: true,
    stack: [
      {
        columns: [
          {
            width: eixo,
            stack: marcas.map((v) => ({
              text: fmtInt(v),
              fontSize: 6.5,
              color: FAINT,
              relativePosition: { x: 0, y: y(v) - 4 },
            })),
          },
          { width: largura, canvas: desenho },
        ],
      },
      {
        margin: [eixo, 3, 0, 0],
        table: {
          widths: dias.map(() => slot - 0.01),
          body: [dias.map((d, i) => ({
            // o último dia fica sem rótulo: na última fatia ele vazaria a margem
            text: i % cada === 0 && i < dias.length - 2 ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '',
            fontSize: 6.5,
            color: FAINT,
            noWrap: true,
          }))],
        },
        layout: { defaultBorder: false, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
      },
      {
        columns: series.map((s) => ({
          width: 'auto',
          columns: [bolinha(s.cor), { width: 'auto', text: s.nome, fontSize: 7.5, color: SUB }],
          columnGap: 3,
        })),
        columnGap: 14,
        margin: [eixo, 6, 0, 0],
      },
    ],
  }
}

function aviso(texto) {
  return { text: texto, fontSize: 8, italics: true, color: SUB, margin: [0, 6, 0, 0] }
}

function documento({ titulo, nomeRelatorio, instancia, periodoLabel, geradoEm, content }) {
  return {
    pageSize: 'A4',
    pageMargins: [40, 64, 40, 48],
    info: { title: titulo, author: NOME_PRODUTO, subject: nomeRelatorio },
    defaultStyle: { font: 'Roboto', fontSize: 8.5, color: INK, lineHeight: 1.25 },
    header: () => ({
      margin: [40, 24, 40, 0],
      stack: [
        {
          columns: [
            { width: '*', ...marca() },
            { width: 'auto', text: `Gerado em ${fmtGeradoEm(geradoEm)}`, fontSize: 7.5, color: FAINT, margin: [0, 3, 0, 0] },
          ],
        },
        regua([0, 9, 0, 0]),
      ],
    }),
    footer: (pagina, paginas) => ({
      margin: [40, 12, 40, 0],
      stack: [
        regua([0, 0, 0, 0]),
        {
          columns: [
            { width: '*', text: `${instancia} · ${nomeRelatorio} · ${periodoLabel}`, fontSize: 7, color: FAINT },
            { width: 'auto', text: `Página ${pagina} de ${paginas}`, fontSize: 7, color: FAINT },
          ],
          margin: [0, 6, 0, 0],
        },
      ],
    }),
    content,
  }
}

// ============================================================================
// Falhas por tipo de erro
// ============================================================================

export function montarVereditoFalhas({ tentativas, motivos }) {
  const total = tentativas.tentativas || 0
  const falhas = tentativas.falhas || 0
  const taxa = total > 0 ? tentativas.enviadas / total : 1
  const deCada100 = Math.floor(taxa * 100)
  const urgentes = motivos.filter((m) => m.prioridade === 'acao')
  const principal = motivos[0]

  if (falhas === 0) {
    return { rotulo: 'TUDO CERTO', cor: OK, texto: [{ text: 'Nenhuma mensagem falhou no período.', bold: true }, ' Tudo o que foi enviado saiu.'] }
  }

  const texto = []
  texto.push(
    taxa >= 0.95
      ? { text: `Quase tudo chegou: ${deCada100} de cada 100 mensagens. `, bold: true }
      : { text: `Só ${deCada100} de cada 100 mensagens chegaram. `, bold: true }
  )
  if (principal) {
    const peso = falhas > 0 ? principal.total / falhas : 0
    texto.push(`A maior parte das falhas (${fmtPct(peso, 0)}) foi “${principal.titulo}”. `)
  }
  if (urgentes.length) {
    texto.push({ text: urgentes.length === 1 ? 'Um ponto pede ação: ' : `${urgentes.length} pontos pedem ação: `, bold: true })
    texto.push(urgentes.map((u) => `“${u.titulo}”`).join(', ') + '.')
  }

  if (urgentes.length) return { rotulo: 'AÇÃO NECESSÁRIA', cor: BAD, texto }
  return { rotulo: 'ATENÇÃO', cor: ATT, texto }
}

export function documentoFalhas({
  instancia,
  metaOficial,
  periodoLabel,
  geradoEm = new Date(),
  tentativas,
  motivos,
  motivosEscolhidos,
  dias,
  porDia,
  lista,
}) {
  const falhas = tentativas.falhas || 0
  const escolhidos = motivosEscolhidos && motivosEscolhidos.length ? motivosEscolhidos : motivos
  const totalEscolhido = escolhidos.reduce((a, m) => a + m.total, 0)
  const maiorMotivo = Math.max(1, ...escolhidos.map((m) => m.total))
  const v = montarVereditoFalhas({ tentativas, motivos })

  const tituloPrincipal = falhas === 0
    ? 'Nenhuma mensagem falhou no período'
    : `Por que ${plural(falhas, 'mensagem não chegou', 'mensagens não chegaram')}`

  const content = [
    ...cabecalho({
      eyebrow: 'Relatório de falhas de envio',
      titulo: tituloPrincipal,
      meta: `Instância ${instancia} · ${metaOficial ? 'API Oficial do WhatsApp' : 'Conexão WhatsApp'} · ${periodoLabel}`,
    }),
    veredito(v),
    blocoNumeros([
      { rotulo: 'Tentativas', valor: fmtInt(tentativas.tentativas), dica: 'mensagens no período' },
      { rotulo: 'Chegaram', valor: fmtInt(tentativas.enviadas), cor: OK, dica: `${fmtPct(tentativas.tentativas ? tentativas.enviadas / tentativas.tentativas : 0)} do total` },
      { rotulo: 'Não chegaram', valor: fmtInt(falhas), cor: BAD, dica: `${fmtPct(tentativas.tentativas ? falhas / tentativas.tentativas : 0)} do total` },
      metaOficial
        ? { rotulo: 'Cobrado pelas falhas', valor: 'R$ 0,00', dica: 'falha não é cobrada' }
        : { rotulo: 'Tipos de falha', valor: fmtInt(motivos.length), dica: 'motivos diferentes' },
    ]),
    ...barraProporcao([
      { rotulo: 'Chegaram', valor: tentativas.enviadas || 0, cor: OK },
      { rotulo: 'Não chegaram', valor: falhas, cor: BAD },
    ]),
  ]

  if (escolhidos.length) {
    content.push(secao(
      'O que aconteceu e o que fazer',
      escolhidos.length < motivos.length ? `${fmtInt(totalEscolhido)} de ${fmtInt(falhas)} falhas · tipos escolhidos na exportação` : ''
    ))
    content.push({
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: [112, 46, '*', '*', 64],
        body: [
          [th('Motivo'), th('Quantas'), th('O que significa'), th('O que fazer'), th('Prioridade')],
          ...escolhidos.map((m) => [
            {
              columns: [
                bolinha(m.cor),
                {
                  width: '*',
                  stack: [
                    { text: m.titulo, bold: true, fontSize: 8.5 },
                    m.codigo ? { text: `código Meta ${m.codigo}`, fontSize: 6.5, color: FAINT, margin: [0, 1, 0, 0] } : '',
                  ],
                },
              ],
              columnGap: 3,
            },
            { stack: [{ text: fmtInt(m.total), bold: true, fontSize: 11 }, barrinha(m.total / maiorMotivo, m.cor, 40)] },
            { text: m.explicacao, fontSize: 8 },
            { text: m.acao, fontSize: 8 },
            chip(PRIORIDADES[m.prioridade].rotulo, PRIORIDADES[m.prioridade].cor, { cheio: false }),
          ]),
        ],
      },
      layout: layoutTabela,
    })
  }

  // Gráfico diário: só faz sentido com alguns dias no período.
  if (dias && dias.length >= 3 && totalEscolhido > 0) {
    const principais = escolhidos.slice(0, 4)
    const resto = escolhidos.slice(4)
    const valoresDe = (chaves) => dias.map((d) =>
      (porDia || []).filter((p) => p.dia === d && chaves.includes(p.chave)).reduce((a, p) => a + p.total, 0))
    const series = principais.map((m) => ({ nome: m.titulo, cor: m.cor, valores: valoresDe(m.chaves) }))
    if (resto.length) {
      series.push({ nome: 'Demais motivos', cor: '#94a3b8', valores: valoresDe(resto.flatMap((m) => m.chaves)) })
    }
    const somaDia = dias.map((_, i) => series.reduce((a, s) => a + s.valores[i], 0))
    const pico = somaDia.indexOf(Math.max(...somaDia))
    const diaPico = dias[pico]
    // título e gráfico juntos: o título sozinho no pé da página não diz nada
    content.push({
      unbreakable: true,
      stack: [
        secao('Falhas por dia', `dia com mais falhas: ${diaPico.slice(8, 10)}/${diaPico.slice(5, 7)}, com ${fmtInt(somaDia[pico])}`),
        graficoPorDia({ dias, series }),
      ],
    })
  }

  // Lista detalhada, agrupada por motivo.
  if (lista && lista.mensagens && lista.mensagens.length) {
    content.push(secao('Lista das mensagens que falharam', 'agrupadas por motivo'))
    const linhas = [[th('Data e hora'), th('Número'), th('Modelo'), th('Motivo')]]
    escolhidos.forEach((m) => {
      const doMotivo = lista.mensagens.filter((msg) => {
        const chave = (msg.erro && (msg.erro.code || msg.erro.text)) || 'desconhecido'
        return m.chaves.includes(chave)
      })
      if (!doMotivo.length) return
      linhas.push([{
        colSpan: 4,
        text: `${m.titulo} · ${plural(m.total, 'mensagem', 'mensagens')}`,
        bold: true,
        fontSize: 8,
        fillColor: tint(m.cor, 0.08),
      }, '', '', ''])
      doMotivo.forEach((msg) => {
        linhas.push([
          { text: fmtDataHora(msg.created_at), fontSize: 8 },
          { text: fmtTelefone(msg.phone), fontSize: 8 },
          { text: msg.templateName || '—', fontSize: 8 },
          { text: m.titulo, fontSize: 8, color: SUB },
        ])
      })
    })
    content.push({ table: { headerRows: 1, dontBreakRows: true, widths: [70, 90, '*', '*'], body: linhas }, layout: layoutTabela })
    if (lista.total > lista.mensagens.length) {
      content.push(aviso(`Mostrando as ${fmtInt(lista.mensagens.length)} falhas mais recentes de ${fmtInt(lista.total)}. Para ver todas, exporte um período menor.`))
    }
  }

  return documento({
    titulo: `Falhas de envio — ${instancia}`,
    nomeRelatorio: 'Falhas de envio',
    instancia,
    periodoLabel,
    geradoEm,
    content,
  })
}

// ============================================================================
// Números sem WhatsApp
// ============================================================================

export function documentoInexistentes({ instancia, periodoLabel, geradoEm = new Date(), numeros, totais, tentativasPeriodo, limite }) {
  const n = totais.numeros || 0
  const peso = tentativasPeriodo && tentativasPeriodo.tentativas ? totais.tentativas / tentativasPeriodo.tentativas : 0

  const content = [
    ...cabecalho({
      eyebrow: 'Relatório de números sem WhatsApp',
      titulo: n === 0 ? 'Nenhum número sem WhatsApp no período' : `${plural(n, 'número da sua base não tem', 'números da sua base não têm')} WhatsApp`,
      meta: `Instância ${instancia} · API Oficial do WhatsApp · ${periodoLabel}`,
    }),
  ]

  if (n === 0) {
    content.push(veredito({ rotulo: 'TUDO CERTO', cor: OK, texto: 'Todas as mensagens do período foram para números com WhatsApp. Não há nada a limpar na base.' }))
    return documento({ titulo: `Números sem WhatsApp — ${instancia}`, nomeRelatorio: 'Números sem WhatsApp', instancia, periodoLabel, geradoEm, content })
  }

  content.push(
    veredito({
      rotulo: 'REVISAR',
      cor: ATT,
      texto: [
        'Mensagens para esses números ',
        { text: 'nunca chegam', bold: true },
        '. Você não pagou por elas, mas cada aviso perdido é um cliente que não ficou sabendo. ',
        { text: 'Atualizar esses cadastros', bold: true },
        ' faz os próximos avisos chegarem a quem precisa. Em casos raros, o número tem WhatsApp, mas o aplicativo do cliente está muito desatualizado.',
      ],
    }),
    blocoNumeros([
      { rotulo: 'Números diferentes', valor: fmtInt(n), cor: BAD, dica: 'sem WhatsApp' },
      { rotulo: 'Tentativas', valor: fmtInt(totais.tentativas), dica: 'mensagens perdidas' },
      { rotulo: 'Insistências', valor: fmtInt(totais.repetidos), dica: 'falharam 2 vezes ou mais' },
      { rotulo: 'Peso nos envios', valor: fmtPct(peso), dica: 'de todas as tentativas' },
    ]),
    secao('Como resolver'),
    {
      columns: [
        ['Confira o cadastro', 'Procure cada número da lista no seu sistema. Muitas vezes falta o 9 ou o DDD está errado.'],
        ['Peça um número atualizado', 'Se o cadastro estiver certo, o cliente trocou de número. Peça o novo no próximo contato.'],
        ['Pare de enviar', 'Enquanto não atualizar, tire o número dos disparos para não repetir a falha.'],
      ].map(([t, d], i) => ({
        table: {
          widths: ['*'],
          body: [[{
            stack: [
              { ...chip(String(i + 1), PRI), margin: [0, 0, 0, 5] },
              { text: t, bold: true, fontSize: 9 },
              { text: d, fontSize: 8, color: SUB, margin: [0, 2, 0, 0] },
            ],
            margin: [8, 8, 8, 8],
          }]],
        },
        layout: { hLineWidth: () => 0.7, vLineWidth: () => 0.7, hLineColor: () => RULE, vLineColor: () => RULE },
      })),
      columnGap: 8,
    }
  )

  const cabecalhoLista = [th('#', 'right'), th('Número'), th('Falhas', 'right'), th('Primeira falha'), th('Última falha'), th('Último modelo')]
  const linha = (r, i) => [
    { text: String(i + 1), fontSize: 8, color: FAINT, alignment: 'right' },
    { text: fmtTelefone(r.telefone), fontSize: 8.5, bold: i < 5 },
    { text: fmtInt(r.tentativas), fontSize: 8.5, alignment: 'right', bold: r.tentativas > 1 },
    { text: fmtDataHora(r.primeira), fontSize: 8 },
    { text: fmtDataHora(r.ultima), fontSize: 8 },
    { text: r.ultimoModelo || '—', fontSize: 8 },
  ]

  content.push(secao('Lista completa', `${plural(n, 'número', 'números')}, dos que mais falharam para os que menos falharam`))
  content.push({
    table: { headerRows: 1, dontBreakRows: true, widths: [16, 92, 34, 62, 62, '*'], body: [cabecalhoLista, ...numeros.map(linha)] },
    layout: layoutTabela,
  })
  if (n > numeros.length) {
    content.push(aviso(`Mostrando ${fmtInt(numeros.length)} de ${fmtInt(n)} números (limite de ${fmtInt(limite || numeros.length)} por PDF). Para ver todos, exporte um período menor.`))
  }

  return documento({ titulo: `Números sem WhatsApp — ${instancia}`, nomeRelatorio: 'Números sem WhatsApp', instancia, periodoLabel, geradoEm, content })
}

// ============================================================================
// Modelos mais disparados
// ============================================================================

export function montarResumoModelos(modelos, totais) {
  if (!modelos.length || !totais.enviadas) return 'Nenhum modelo foi enviado no período.'
  const partes = []
  const top = modelos[0]
  partes.push({ text: `O modelo “${top.nome}” foi ${fmtPct(top.enviadas / totais.enviadas, 0)} de tudo o que você enviou. `, bold: true })

  const mkt = modelos.filter((m) => /^MARKETING/.test(String(m.categoria || '').toUpperCase()))
  const mktEnviadas = mkt.reduce((a, m) => a + m.enviadas, 0)
  const custoDe = (m) => (m.custo !== null && m.custo !== undefined ? m.custo : m.custoMeta ? m.custoMeta.total : null)
  const custoTotal = modelos.reduce((a, m) => a + (custoDe(m) || 0), 0)
  const mktCusto = mkt.reduce((a, m) => a + (custoDe(m) || 0), 0)
  if (mktEnviadas > 0 && custoTotal > 0) {
    partes.push(`As promoções (Marketing) foram ${fmtPct(mktEnviadas / totais.enviadas, 0)} dos envios, mas ${fmtPct(mktCusto / custoTotal, 0)} do custo. `)
  }

  const comLeitura = modelos.filter((m) => m.entregues >= 20)
  if (comLeitura.length >= 2) {
    const menos = comLeitura.reduce((a, m) => (m.lidas / m.entregues < a.lidas / a.entregues ? m : a))
    partes.push(`O menos lido foi “${menos.nome}”: ${fmtPct(menos.lidas / menos.entregues, 0)} de quem recebeu abriu.`)
  }
  return partes
}

export function documentoModelos({ instancia, periodoLabel, geradoEm = new Date(), modelos, totais }) {
  const temCusto = modelos.some((m) => m.custo !== null && m.custo !== undefined)
  const temCustoMeta = !temCusto && modelos.some((m) => m.custoMeta)
  const custoTotal = temCusto ? totais.custo || 0 : temCustoMeta ? totais.custoMeta || 0 : null
  const maior = Math.max(1, ...modelos.map((m) => m.enviadas))
  const cat = (m) => CATEGORIAS[String(m.categoria || '').toUpperCase()] || null

  const content = [
    ...cabecalho({
      eyebrow: 'Relatório de modelos',
      titulo: 'Quais mensagens você mais enviou',
      meta: `Instância ${instancia} · API Oficial do WhatsApp · ${periodoLabel}`,
    }),
    veredito({ rotulo: 'RESUMO', cor: PRI, texto: montarResumoModelos(modelos, totais) }),
    blocoNumeros([
      { rotulo: 'Modelos usados', valor: fmtInt(modelos.length), dica: 'no período' },
      { rotulo: 'Mensagens enviadas', valor: fmtInt(totais.enviadas), dica: 'que saíram com sucesso' },
      custoTotal !== null
        ? { rotulo: temCusto ? 'Custo total' : 'Custo na Meta', valor: fmtBRL(custoTotal), dica: temCusto ? 'debitado do saldo' : 'estimado pela tabela da Meta' }
        : { rotulo: 'Entregues', valor: fmtInt(totais.entregues), dica: 'chegaram no aparelho' },
      custoTotal !== null && totais.enviadas
        ? { rotulo: 'Custo médio', valor: fmtBRL(custoTotal / totais.enviadas, 3), dica: 'por mensagem' }
        : { rotulo: 'Lidas', valor: fmtInt(totais.lidas), dica: 'abertas pelo cliente' },
    ]),
    secao('Ranking por volume'),
    {
      table: {
        widths: [150, 290, '*'],
        body: modelos.slice(0, 10).map((m) => [
          {
            stack: [
              { text: m.nome, bold: true, fontSize: 8.5 },
              { text: cat(m) ? cat(m).rotulo : 'Sem categoria', fontSize: 7, color: FAINT },
            ],
          },
          { canvas: [{ type: 'rect', x: 0, y: 3, w: Math.max(3, (m.enviadas / maior) * 285), h: 10, r: 2, color: cat(m) ? cat(m).cor : '#94a3b8' }] },
          { text: fmtInt(m.enviadas), bold: true, alignment: 'right', fontSize: 9, margin: [0, 2, 0, 0] },
        ]),
      },
      layout: { defaultBorder: false, paddingTop: () => 3, paddingBottom: () => 3, paddingLeft: () => 0, paddingRight: () => 4 },
    },
    secao('Desempenho e custo de cada modelo'),
  ]

  const cab = [th('Modelo'), th('Tipo'), th('Enviadas', 'right'), th('Chegaram', 'right'), th('Lidas', 'right'), th('Falharam', 'right')]
  if (temCusto) cab.push(th('Custo', 'right'), th('Por msg', 'right'))
  if (temCustoMeta) cab.push(th('Custo Meta*', 'right'))
  const larguras = ['*', 58, 44, 44, 34, 40]
  if (temCusto) larguras.push(54, 40)
  if (temCustoMeta) larguras.push(58)

  content.push({
    table: {
      headerRows: 1,
      dontBreakRows: true,
      widths: larguras,
      body: [
        cab,
        ...modelos.map((m) => {
          const linha = [
            { text: m.nome, bold: true, fontSize: 8 },
            cat(m) ? chip(cat(m).rotulo, cat(m).cor, { cheio: false }) : { text: '—', color: FAINT },
            { text: fmtInt(m.enviadas), alignment: 'right' },
            { text: m.enviadas ? fmtPct(m.entregues / m.enviadas) : '—', alignment: 'right' },
            { text: m.entregues ? fmtPct(m.lidas / m.entregues, 0) : '—', alignment: 'right' },
            { text: fmtInt(m.falhas), alignment: 'right', color: m.falhas ? BAD : INK },
          ]
          if (temCusto) linha.push({ text: fmtBRL(m.custo), alignment: 'right', bold: true }, { text: fmtPreco(m.custoPorMsg), alignment: 'right' })
          if (temCustoMeta) linha.push({ text: m.custoMeta ? fmtBRL(m.custoMeta.total) : '—', alignment: 'right' })
          return linha
        }),
      ],
    },
    layout: layoutTabela,
  })
  content.push(aviso('“Chegaram” é sobre as enviadas; “Lidas” é sobre as que chegaram (quem desativou a confirmação de leitura não aparece como lida).'))
  if (temCustoMeta) content.push(aviso('* Custo na Meta estimado pela tabela oficial de preços; é pago direto no seu cartão cadastrado na Meta.'))

  // Um quadro por tipo (Marketing e Marketing Lite contam como um só).
  const tipos = Array.from(new Map(modelos.map((m) => cat(m)).filter(Boolean).map((t) => [t.rotulo, t])).values())
  if (tipos.length) {
    content.push(secao('Entenda os tipos de mensagem'))
    content.push({
      columns: tipos.map((t) => ({
        table: {
          widths: ['*'],
          body: [[{
            fillColor: tint(t.cor, 0.08),
            margin: [8, 7, 8, 7],
            stack: [
              { columns: [bolinha(t.cor), { width: '*', text: t.rotulo, bold: true, fontSize: 9 }], columnGap: 2 },
              { text: t.explica, fontSize: 7.5, color: SUB, margin: [0, 3, 0, 0] },
            ],
          }]],
        },
        layout: 'noBorders',
      })),
      columnGap: 8,
    })
  }

  return documento({ titulo: `Modelos mais disparados — ${instancia}`, nomeRelatorio: 'Modelos mais disparados', instancia, periodoLabel, geradoEm, content })
}

const documentosLib = { documentoFalhas, documentoInexistentes, documentoModelos }

export default documentosLib
